import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Shield } from 'lucide-react';

const PolitiqueConfidentialite = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 mt-20">
        <Card className="max-w-4xl mx-auto shadow-xl">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-600" />
              <CardTitle className="text-3xl font-bold">Politique de Confidentialité</CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="p-8 space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-gray-700">
                L'association L'Alliance Humaine 2020, responsable du site Alliance Solidarité, accorde une grande importance à la protection de vos données personnelles. Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons vos données.
              </p>
            </section>

            {/* Responsable du traitement */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">1. Responsable du traitement</h2>
              <div className="space-y-2 text-gray-700">
                <p><strong>Raison sociale :</strong> L'Alliance Humaine 2020</p>
                <p><strong>Siège social :</strong> 32 Allée Bajou, 94230 Cachan</p>
                <p><strong>Email :</strong> contact@alliance-solidarite.org</p>
                <p><strong>Téléphone :</strong> 06 41 95 65 75</p>
              </div>
            </section>

            {/* Données collectées */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">2. Données collectées</h2>
              
              <h3 className="text-xl font-semibold mb-3 text-gray-800">2.1 Données d'inscription</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone (optionnel)</li>
                <li>Adresse postale (optionnel)</li>
                <li>Mot de passe (crypté)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">2.2 Données de navigation</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Adresse IP</li>
                <li>Données de localisation (avec votre consentement)</li>
                <li>Pages visitées</li>
                <li>Cookies techniques</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 text-gray-800">2.3 Données d'utilisation</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Offres créées</li>
                <li>Messages envoyés</li>
                <li>Commentaires publiés</li>
                <li>Favoris enregistrés</li>
              </ul>
            </section>

            {/* Finalités */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">3. Finalités du traitement</h2>
              <p className="text-gray-700 mb-3">Vos données sont collectées pour :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Gestion du compte utilisateur</strong> : création, authentification, personnalisation</li>
                <li><strong>Fonctionnement du service</strong> : publication d'offres, messagerie, notifications</li>
                <li><strong>Sécurité</strong> : prévention des fraudes, modération, bannissements</li>
                <li><strong>Communication</strong> : emails de notification, newsletters (avec consentement)</li>
                <li><strong>Amélioration du service</strong> : statistiques anonymisées, analyse des usages</li>
              </ul>
            </section>

            {/* Base légale */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">4. Base légale</h2>
              <p className="text-gray-700 mb-3">Le traitement de vos données repose sur :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Votre consentement</strong> : lors de votre inscription et pour les communications marketing</li>
                <li><strong>L'exécution du contrat</strong> : fourniture des services de la plateforme</li>
                <li><strong>L'intérêt légitime</strong> : sécurité, prévention des abus, amélioration du service</li>
                <li><strong>Obligation légale</strong> : conservation des données pour répondre aux obligations légales</li>
              </ul>
            </section>

            {/* Destinataires */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">5. Destinataires des données</h2>
              <p className="text-gray-700 mb-3">Vos données sont accessibles à :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>L'équipe de l'association</strong> : administrateurs et modérateurs</li>
                <li><strong>Prestataires techniques</strong> : hébergement (Emergent AI), emails (Resend)</li>
                <li><strong>Autres utilisateurs</strong> : uniquement les données de profil public (nom, offres publiées)</li>
              </ul>
              <p className="text-gray-700 mt-3">
                Vos données ne sont jamais vendues à des tiers.
              </p>
            </section>

            {/* Durée de conservation */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">6. Durée de conservation</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Compte actif</strong> : pendant toute la durée d'utilisation du service</li>
                <li><strong>Compte inactif</strong> : 3 ans après la dernière connexion</li>
                <li><strong>Logs de connexion</strong> : 1 an</li>
                <li><strong>Données de modération</strong> : 3 ans (conformité légale)</li>
              </ul>
            </section>

            {/* Vos droits */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">7. Vos droits (RGPD)</h2>
              <p className="text-gray-700 mb-3">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Droit d'accès</strong> : obtenir une copie de vos données</li>
                <li><strong>Droit de rectification</strong> : corriger des données inexactes</li>
                <li><strong>Droit à l'effacement</strong> : supprimer vos données (« droit à l'oubli »)</li>
                <li><strong>Droit d'opposition</strong> : refuser le traitement de vos données</li>
                <li><strong>Droit à la limitation</strong> : limiter le traitement dans certains cas</li>
                <li><strong>Droit à la portabilité</strong> : récupérer vos données dans un format structuré</li>
                <li><strong>Droit de retirer votre consentement</strong> : à tout moment</li>
              </ul>
              
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <p className="text-gray-700">
                  <strong>Pour exercer ces droits :</strong><br/>
                  Envoyez un email à <a href="mailto:contact@alliance-solidarite.org" className="text-purple-600 hover:underline">contact@alliance-solidarite.org</a> avec votre demande et une pièce d'identité.
                </p>
                <p className="text-gray-700 mt-2">
                  Nous répondrons dans un délai maximum de 1 mois.
                </p>
              </div>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">8. Cookies</h2>
              <p className="text-gray-700 mb-3">Nous utilisons les cookies suivants :</p>
              
              <h3 className="text-xl font-semibold mb-2 text-gray-800">8.1 Cookies essentiels</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                <li>Cookie d'authentification (JWT token)</li>
                <li>Préférences de langue</li>
              </ul>

              <h3 className="text-xl font-semibold mb-2 text-gray-800">8.2 Cookies de fonctionnement</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Mémorisation du thème (jour/nuit)</li>
                <li>Sauvegarde de la position sur la carte</li>
              </ul>

              <p className="text-gray-700 mt-3">
                Vous pouvez désactiver les cookies dans les paramètres de votre navigateur.
              </p>
            </section>

            {/* Sécurité */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">9. Sécurité</h2>
              <p className="text-gray-700 mb-3">Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Cryptage des mots de passe (bcrypt)</li>
                <li>Connexions sécurisées (HTTPS)</li>
                <li>Authentification JWT</li>
                <li>Accès restreint aux données (rôles utilisateurs)</li>
                <li>Sauvegardes régulières</li>
                <li>Surveillance des accès</li>
              </ul>
            </section>

            {/* Transferts hors UE */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">10. Transferts hors UE</h2>
              <p className="text-gray-700">
                Certains de nos prestataires (Resend) peuvent être situés hors de l'Union Européenne. Dans ce cas, nous nous assurons que des garanties appropriées sont mises en place (clauses contractuelles types, Privacy Shield).
              </p>
            </section>

            {/* Réclamation */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">11. Réclamation auprès de la CNIL</h2>
              <p className="text-gray-700">
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg text-gray-700">
                <p><strong>CNIL</strong></p>
                <p>3 Place de Fontenoy - TSA 80715</p>
                <p>75334 PARIS CEDEX 07</p>
                <p>Tél : 01 53 73 22 22</p>
                <p>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">www.cnil.fr</a></p>
              </div>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-600">12. Modifications</h2>
              <p className="text-gray-700">
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Les modifications seront notifiées par email et/ou via une notification sur le site.
              </p>
            </section>

            {/* Date */}
            <section className="pt-4 border-t">
              <p className="text-sm text-gray-500">
                Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </section>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
